import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import Select, { RefSelectProps } from "antd/es/select";
import cn from "classnames";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import { Route } from "@mapmycustomers/shared/types/entity";
import { LegacySimpleCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";

import EntityBadge from "@app/component/EntityBadge";
import { fetchRoutes } from "@app/store/routes/actions";

import styles from "./RoutesFilter.module.scss";

interface RoutesFilterProps {
  className?: string;
  disabled?: boolean;
  focus?: boolean;
  onChange?: (value: LegacySimpleCondition) => void;
  onFetchRoutes?: (payload: { query?: string; selectedRouteIds?: Route["id"][] }) => void;
  options: Partial<Record<FilterOption, unknown>> | undefined;
  placeholder?: ReactNode;
  value: LegacySimpleCondition;
}

const RoutesFilter = ({
  className,
  disabled,
  focus,
  onChange,
  onFetchRoutes,
  options,
  placeholder,
  value,
}: RoutesFilterProps) => {
  const intl = useIntl();

  useEffect(() => {
    onFetchRoutes?.({
      selectedRouteIds: Array.isArray(value.value) ? value.value : undefined,
    });
  }, [onFetchRoutes, value.value]);

  const setRef = useCallback(
    (ref: null | RefSelectProps) => {
      if (focus && ref) {
        ref.focus();
      }
    },
    [focus]
  );

  const handleSearch = useDebouncedCallback(
    [
      (query: string) => {
        onFetchRoutes?.({
          query,
          selectedRouteIds: Array.isArray(value.value) ? value.value : undefined,
        });
      },
      100,
    ],
    [onFetchRoutes, value.value]
  );

  const handleBlur = useCallback(() => {
    onFetchRoutes?.({
      selectedRouteIds: Array.isArray(value.value) ? value.value : undefined,
    });
  }, [onFetchRoutes, value.value]);

  const selectOptions = useMemo(
    () =>
      ((options?.availableRoutes as Route[]) ?? []).map((route) => ({
        label: <EntityBadge entity={{ color: Color.GREY, name: route.name }} />,
        text: route.name,
        value: route.id,
      })),
    [options?.availableRoutes]
  );

  return (
    <Select<Array<Route["id"]>>
      className={cn(styles.container, className)}
      disabled={disabled}
      filterOption={defaultFilterOption}
      mode="multiple"
      onBlur={handleBlur}
      onChange={(routeIds: Route["id"][]) => onChange?.({ ...value, value: routeIds })}
      onSearch={handleSearch}
      options={selectOptions}
      placeholder={
        placeholder ??
        intl.formatMessage({
          id: "filters.route.select.placeholder",
          defaultMessage: "Click or type to select routes",
          description: "Placeholder displayed in a RouteFilter's select field",
        })
      }
      ref={setRef}
      value={Array.isArray(value.value) ? value.value : []}
    />
  );
};

const mapDispatchToProps = {
  onFetchRoutes: fetchRoutes.request,
};

export default connect(null, mapDispatchToProps)(RoutesFilter);
